import graphql__dc63aca6087276e5863e from "./__generated__/AuthenticationAnonymousUserForm_authenticationConfig.graphql.js";import { useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useFormatMessage } from "../Intl/Context";
import withFormStore from "../Form/withFormStore";
import withAlert from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR } from "../Alerts/Alert";
import StringField from "../Form/Field/StringField";
import EmailField from "../Form/Field/EmailField";
import CheckBoxField from "../Form/Field/CheckBoxField";
import EmailTrackingConsentField from "../Form/Field/EmailTrackingConsentField";
import Button from "../Buttons/Button";
import Answers from "../Answer/Answers";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";

import { anonymousUserAuth } from "../../mutations/AnonymousUserAuthMutation";

const AuthenticationAnonymousUserForm = props => {
  const authenticationConfig = useFragment(
    AuthenticationAnonymousUserForm_authenticationConfig,
    props.authenticationConfig
  );
  const {
    forceEmailForAnonymousUsers,
    anonymousUserSubscriptionFormResponse,
    enableEmailConsent,
    emailConsentLabel
  } = authenticationConfig;

  const {
    validateForm,
    authenticationAdditionalProps,
    formState,
    onSignIn,
    showAlert,
    viewer
  } = props;

  const forceEmail =
    forceEmailForAnonymousUsers ||
    authenticationAdditionalProps.forceEmailForAnonymousUsers;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = useFormatMessage();
  const title = t(intlMessages.title, {
    b: message => `<b>${message}</b>`,
    br: "<br>"
  });
  const requiredFieldsHint = t(formPageIntlMessages.requiredFieldsHint);
  const name = t(intlMessages.name);
  const email = t(intlMessages.email);
  const nameHint = t(intlMessages.nameHint);
  const emailOptionalHint = t(intlMessages.emailOptionalHint);
  const emailRequiredHint = t(intlMessages.emailRequiredHint);
  const submit = t(intlMessages.submit);

  const answers = anonymousUserSubscriptionFormResponse
    ? anonymousUserSubscriptionFormResponse.answers
    : [];
  const emailHint = forceEmail ? emailRequiredHint : emailOptionalHint;

  const handleSignInResponse = ({ anonymousUserAuth }) => {
    setIsSubmitting(false);

    if (anonymousUserAuth.success) {
      if (onSignIn) {
        onSignIn();
      } else {
        window.location.href = window.location;
      }
    } else {
      showAlert({
        message: anonymousUserAuth.notice,
        type: ALERT_TYPE_ERROR
      });
    }
  };

  const submitForm = () => {
    // Do not submit form if it is already submitting
    if (isSubmitting) return;

    if (validateForm()) {
      // If a contribution id was provided through the authenticationAdditionalProps
      // object fetch it to provide it to the mutation so the authentication step is not
      // shown to the viewer for the next steps of the current contribution.
      const { contributionId } = authenticationAdditionalProps;

      setIsSubmitting(true);

      anonymousUserAuth(formState, contributionId, handleSignInResponse);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    submitForm();
  };

  const className = classNames(["form-wrapper", props.className]);

  const formClassName = classNames({
    "authentication-fieldset": true,
    "authentication-sign-in": true,
    box: true,
    "margin-bottom-xs": true,
    "padding-md": true,
    loading: isSubmitting
  });

  return (
    <div className={className} data-anonymous-user-auth-form>
      <form className={formClassName} onSubmit={handleSubmit}>
        <fieldset>
          <legend
            className="text-subtitle-page"
            dangerouslySetInnerHTML={{ __html: title }}
          />

          <p
            className="form-hint-required-fields margin-bottom-sm text-small"
            dangerouslySetInnerHTML={{ __html: requiredFieldsHint }}
          />

          <StringField
            fieldName="name"
            label={name}
            hint={nameHint}
            required
            autoFocus
            defaultValue={viewer.name}
          />

          <EmailField
            fieldName="email"
            label={email}
            hint={emailHint}
            required={forceEmail}
            defaultValue={viewer.email}
            autoComplete="email"
          />

          <div className="checkboxes-no-background">
            {enableEmailConsent && (
              <CheckBoxField
                fieldName="emailConsent"
                label={emailConsentLabel}
                defaultChecked={viewer?.emailConsent}
              />
            )}

            <EmailTrackingConsentField defaultChecked={viewer?.emailTrackingConsent} />
          </div>

          <Answers answers={answers} />
        </fieldset>

        <Button
          type="submit"
          className="btn btn-primary btn-block-md"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={submit}
          iconClassName="icon-arrow-right"
          iconPosition="after"
          data-anonymous-user-authenticate-button
        />
      </form>
    </div>
  );
};

const AuthenticationAnonymousUserForm_authenticationConfig = graphql__dc63aca6087276e5863e;

export default withFormStore(withAlert(AuthenticationAnonymousUserForm));

const intlMessages = defineMessages({
  title: {
    id: "authentication.anonymousUserAuth.title",
    defaultMessage: "Sans créer de compte ?{br}<b>Renseignez quelques infos ...</b>"
  },
  name: {
    id: "authentication.anonymousUserAuth.name",
    defaultMessage: "Pseudo"
  },
  email: {
    id: "authentication.anonymousUserAuth.email",
    defaultMessage: "E-mail"
  },
  nameHint: {
    id: "authentication.anonymousUserAuth.nameHint",
    defaultMessage: "Ce nom sera utilisé publiquement"
  },
  emailOptionalHint: {
    id: "authentication.anonymousUserAuth.emailOptionalHint",
    defaultMessage:
      "Optionnel. Permet de vous notifier lors des réponses à vos commentaires."
  },
  emailRequiredHint: {
    id: "authentication.anonymousUserAuth.emailRequiredHint",
    defaultMessage:
      "Obligatoire. Permet de vous notifier lors des réponses à vos commentaires."
  },
  submit: {
    id: "authentication.anonymousUserAuth.submit",
    defaultMessage: "Valider"
  }
});
